import React, { useMemo } from 'react';
import { MANAGE_ORDERS_LABELS_ID } from '../../../../config/constants';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Typography, Loader, Accordion, useAccordionGroup } from '@lucid/lego';
import { useGetOrderDetails } from '../../../../api/services/lupp/order/order';
import { DOCUMENT_CATEGORY } from '../../../../constants/constants';
import { groupFilesByCategory } from '../../../../api/services/lupp/order/transformers.order';
import { useStore } from '../../../../store/store';
import { useLabels } from '../../../../hooks/useLabels';
import { useContentfulLocale } from '../../../../hooks/useContentfulLocale';
import { convertToContentfulLocale } from '../../../../utils/tools';
import ProForma from './ProForma/ProForma';
import PaymentFinancing from './PaymentFinancing/PaymentFinancing';
import { isEmbeddedSubventionMethod } from './PaymentFinancing/constants';
import Registration from './Registration/Registration';
import QualityCheck from './QualityCheck/QualityCheck';
import CustomerHandoff from './CustomerHandoff/CustomerHandoff';
import styles from './Details.module.scss';
import Overview from './Overview/Overview';

const ACCORDION_KEYS = {
  OVERVIEW: 'overview',
  PROFORMA: 'proforma',
  PAYMENT: 'payment',
  REGISTRATION: 'registration',
  QUALITY_CHECK: 'quality-check',
  CUSTOMER_HANDOFF: 'customer-handoff',
} as const;

/**
 * Order Details page component
 * Displays comprehensive order information in a full page view
 *
 * @component
 * @returns JSX.Element
 *
 * @example
 * ```tsx
 * // Accessed via route: /order-hub/manage-orders/:orderId
 * <Details />
 * ```
 */
const Details: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const contentfulLocale = useContentfulLocale();
  const location = useLocation();
  const locationState = location.state as { orderNumber?: string } | null;

  // Labels
  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useLabels({
    id: MANAGE_ORDERS_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  const { data: orderData, isLoading, error } = useGetOrderDetails(String(orderId));

  // Group all file references once; children receive the pre-grouped map
  const filesByCategory = useMemo(
    () => groupFilesByCategory(orderData?.moduleFileReferenceDtoList),
    [orderData?.moduleFileReferenceDtoList]
  );

  // Derive order title from API response or fallback to location state
  const orderTitle = useMemo(() => {
    const orderNum = orderData?.orderNumber || locationState?.orderNumber;
    return orderNum ? `Order ${orderNum}` : 'Order';
  }, [orderData?.orderNumber, locationState?.orderNumber]);

  const { getAccordionProps } = useAccordionGroup({ defaultOpen: ACCORDION_KEYS.OVERVIEW });

  return (
    <>
      <Box>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {orderTitle}
        </Typography>
        <Typography variant="body" size="medium" theme={theme} color="gray-3">
          {labels?.orderDetailsSubtitle || 'View and/or edit order information.'}
        </Typography>
      </Box>

      <Box mt={24}>
        {(isLoading || labelsLoading) && (
          <Box className={styles.loadingContainer}>
            <Loader size={48} theme={theme} />
          </Box>
        )}

        {(error || labelsError) && (
          <Box className={styles.errorContainer}>
            <Typography variant="body" size="large" theme={theme} color="error-1">
              {labels?.errorLoadingOrder ||
                'We were unable to load these order details. Please refresh and try again.'}
            </Typography>
          </Box>
        )}

        {!isLoading && !labelsLoading && !error && !labelsError && orderData && (
          <>
            <Box className={styles.accordionSection}>
              <Accordion
                title={labels?.orderCustomerOverview || 'Order & Customer Overview'}
                theme={theme}
                {...getAccordionProps(ACCORDION_KEYS.OVERVIEW)}
              >
                <Overview
                  orderData={orderData}
                  filesByCategory={filesByCategory}
                  theme={theme}
                  labels={labels || {}}
                  dataTestId="order-customer-overview"
                />
              </Accordion>
            </Box>
            <Box className={styles.accordionSection}>
              <Accordion
                title={labels?.proformaInvoice || 'Pro-Forma Invoice'}
                theme={theme}
                {...getAccordionProps(ACCORDION_KEYS.PROFORMA)}
              >
                <ProForma
                  orderMasterIdentifier={orderId || null}
                  sfdcDeliveryIdentifier={orderData.sfdcDeliveryIdentifier}
                  invoices={filesByCategory[DOCUMENT_CATEGORY.PROFORMA_INVOICE] ?? []}
                  isEmbeddedFinance={isEmbeddedSubventionMethod(orderData.subventionMethod)}
                  theme={theme}
                  labels={labels || {}}
                  locale={contentfulLocale}
                  dataTestId="proforma-invoice"
                />
              </Accordion>
            </Box>

            <Box className={styles.accordionSection}>
              <Accordion
                title={labels?.paymentFinancing || 'Payment & Financing'}
                theme={theme}
                {...getAccordionProps(ACCORDION_KEYS.PAYMENT)}
              >
                <PaymentFinancing
                  orderData={orderData}
                  theme={theme}
                  labels={labels || {}}
                  dataTestId="payment-financing"
                />
              </Accordion>
            </Box>

            <Box className={styles.accordionSection}>
              <Accordion
                title={labels?.registrationDetails || 'Registration Details & Pre-Delivery'}
                theme={theme}
                {...getAccordionProps(ACCORDION_KEYS.REGISTRATION)}
              >
                <Registration
                  orderData={orderData}
                  theme={theme}
                  labels={labels || {}}
                  dataTestId="registration-details"
                />
              </Accordion>
            </Box>

            <Box className={styles.accordionSection}>
              <Accordion
                title={labels?.finalQualityCheck || 'Final Quality Check'}
                theme={theme}
                {...getAccordionProps(ACCORDION_KEYS.QUALITY_CHECK)}
              >
                <QualityCheck
                  orderData={orderData}
                  filesByCategory={filesByCategory}
                  theme={theme}
                  labels={labels || {}}
                  dataTestId="final-quality-check"
                />
              </Accordion>
            </Box>

            <Box className={styles.accordionSection}>
              <Accordion
                title={labels?.customerHandoff || 'Customer Handoff'}
                theme={theme}
                {...getAccordionProps(ACCORDION_KEYS.CUSTOMER_HANDOFF)}
              >
                <CustomerHandoff
                  orderData={orderData}
                  filesByCategory={filesByCategory}
                  theme={theme}
                  labels={labels || {}}
                  dataTestId="customer-handoff"
                />
              </Accordion>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

Details.displayName = 'Details';

export default Details;
